import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Col,
  Divider,
  Drawer,
  Form,
  Modal,
  Rate,
  Row,
  Table,
  Tag,
  Typography,
  Descriptions,
  Space,
  Badge,
  Skeleton,
} from 'antd';
import { useTranslation } from 'react-i18next';
import ErasmusUniversitiesEdit from '../ErasmusUniversitiesEdit.jsx';
import { CollapseBlock } from '../../../../../../common/index';
import FormCustomRating from '../../../../../../antd/form/FormCustomRating/FormCustomRating';
import ErasmusSubjectsTable from '../../../ErasmusSubjects/ErasmusSubjectsTable';

const UniversityModal = ({
  visible,
  toggleModal,
  title,
  id,
  university,
  isLoading,
  variant = 'modal', // 'modal' | 'drawer'
  width,
}) => {
  const { t } = useTranslation();
  const { Text, Title } = Typography;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const contentStyle = {
    position: 'relative',
    borderRadius: 12,
    overflow: 'hidden',
  };
  const bannerImgStyle = {
    width: '100%',
    maxHeight: 300,
    objectFit: 'cover',
    display: 'block',
  };
  const bannerOverlay = {
    position: 'absolute',
    right: 12,
    bottom: 12,
    background: 'rgba(0,0,0,.55)',
    color: '#fff',
    padding: '10px 14px',
    borderRadius: 10,
  };
  const headerStyle = { fontSize: 16, color: '#1a1a1a' };
  const textStyle = {
    fontSize: 16,
    color: '#595959',
    display: 'block',
    marginTop: 4,
  };
  const rowStyle = { background: '#fff', padding: 0, borderRadius: 12 };

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const baseUrl = currentUrl
    ? currentUrl.replace('/cms/erasmus/dashboard', '')
    : '';

  // ===== helpers pre subject preview =====
  const pickFirstLocal = (maybeArr) => {
    const arr = Array.isArray(maybeArr) ? maybeArr : [];
    return arr.length ? arr[0] : null;
  };
  const findPivotByRowId = (rowId) =>
    (university?.erasmus_subjects_pivot || []).find((p) => p?.id === rowId);
  const findPivotsByErasmusId = (erasmusId) =>
    (university?.erasmus_subjects_pivot || []).filter(
      (p) => p?.erasmus_subject_id === erasmusId,
    );
  const findSubjectById = (sid) =>
    (university?.subjects || []).find((s) => s?.id === sid);
  const findSubjectByName = (name) =>
    (university?.subjects || []).find(
      (s) => (s?.name || '').trim() === (name || '').trim(),
    );

  const handlePreviewSubject = (row) => {
    let erasmusSubjectId = row?.erasmus_subject_id;

    if (!erasmusSubjectId && row?.id != null) {
      const p = findPivotByRowId(row.id);
      if (p?.erasmus_subject_id) erasmusSubjectId = p.erasmus_subject_id;
    }
    if (!erasmusSubjectId) {
      const guess = findSubjectByName(row?.name);
      if (guess?.id) erasmusSubjectId = guess.id;
    }

    const subject = erasmusSubjectId ? findSubjectById(erasmusSubjectId) : null;
    const pivots = erasmusSubjectId
      ? findPivotsByErasmusId(erasmusSubjectId)
      : [];

    const baseName = subject?.name ?? row?.name ?? '';
    const academic_year = subject?.academic_year ?? '';
    const credits = subject?.credits ?? '';
    const recognized_credits = subject?.recognized_credits ?? '';
    const marks_average = subject?.marks_average ?? '';
    const range = subject?.range ?? '';

    let local_code = row?.local_subject_code || '';
    let local_name = row?.local_subject_name || '';
    if (!local_code || !local_name) {
      const lsFromSubject = pickFirstLocal(subject?.local_subjects);
      if (lsFromSubject) {
        if (!local_code) local_code = lsFromSubject?.code || '';
        if (!local_name) local_name = lsFromSubject?.name || '';
      }
    }
    if ((!local_code || !local_name) && pivots.length > 0) {
      const ls =
        pivots[0]?.local_subject || pickFirstLocal(pivots[0]?.local_subjects);
      if (ls) {
        if (!local_code) local_code = ls?.code || '';
        if (!local_name) local_name = ls?.name || '';
      }
    }

    const hasFriRef =
      !!local_code ||
      !!local_name ||
      (Array.isArray(subject?.local_subjects) &&
        subject.local_subjects.length > 0) ||
      pivots.some(
        (p) =>
          !!p?.local_subject_id ||
          !!p?.local_subject?.code ||
          (Array.isArray(p?.local_subjects) && p.local_subjects.length > 0),
      );
    const faculty = hasFriRef ? 'FRI' : 'UNIZA';

    const mappingsRaw = pivots.map((p) => {
      const ls = p?.local_subject || pickFirstLocal(p?.local_subjects);
      return {
        id:
          p?.local_subject_id ??
          ls?.id ??
          p?.id ??
          `${p?.erasmus_subject_id}-${p?.local_subject_id ?? 'none'}`,
        code: ls?.code ?? '',
        name: ls?.name ?? '',
      };
    });
    const uniqueMappings = Array.from(
      new Map(
        mappingsRaw.map((m) => [
          `${(m.code || '').toLowerCase()}|${(m.name || '').toLowerCase()}`,
          m,
        ]),
      ).values(),
    );

    setPreviewData({
      id: subject?.id ?? erasmusSubjectId ?? row?.id,
      name: baseName,
      academic_year,
      credits,
      recognized_credits,
      marks_average,
      range,
      university_name: university?.name ?? row?.university ?? '',
      faculty,
      local_subject_code: local_code,
      local_subject_name: local_name,
      mappings: uniqueMappings,
      raw: { row, subject, pivots },
    });
    setPreviewOpen(true);
  };

  const mappingColumns = useMemo(
    () => [
      {
        title: t('Kód predmetu FRI'),
        dataIndex: 'code',
        key: 'code',
        ellipsis: true,
      },
      {
        title: t('Názov predmetu FRI'),
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
      },
    ],
    [t],
  );

  // ===== spoločné telo (banner + základ + ratingy + predmety – predmety iba vo variante 'drawer') =====
  const Body = () => {
    if (isLoading) return <Skeleton active paragraph={{ rows: 8 }} />;
    if (!university) return <ErasmusUniversitiesEdit mode="preview" id={id} />;

    return (
      <div key={university.id}>
        {/* Banner */}
        <div style={contentStyle}>
          <img
            src={
              university?.title_photo_path?.includes('/storage/universities')
                ? university?.title_photo_path
                : `${baseUrl}/storage/mobility/universities/${university?.title_photo_path}`
            }
            alt={university.name}
            style={bannerImgStyle}
          />
          <div style={bannerOverlay}>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>
              {university.name}
            </div>
            <Rate disabled defaultValue={university.average_rating} />
          </div>
        </div>

        {/* Info bloky */}
        <CollapseBlock>
          <Row gutter={[24, 24]} style={rowStyle}>
            <Col span={8}>
              <Text strong style={headerStyle}>
                {t('Krajina')}:
              </Text>
              <Text style={textStyle}>{university.country}</Text>
            </Col>
            <Col span={8}>
              <Text strong style={headerStyle}>
                {t('Mesto')}:
              </Text>
              <Text style={textStyle}>{university.city}</Text>
            </Col>
            <Col span={8}>
              <Text strong style={headerStyle}>
                Web:
              </Text>
              <Text style={textStyle}>
                <a href={university.website} target="_blank" rel="noreferrer">
                  {university.website}
                </a>
              </Text>
            </Col>
          </Row>
          <Divider />
          <Form initialValues={university}>
            <Row gutter={[24, 24]} style={rowStyle}>
              <Col span={8}>
                <Text strong style={headerStyle}>
                  {t('Vzdelávanie')}:
                </Text>
                <FormCustomRating name="subjects_rating" disabled />
              </Col>
              <Col span={8}>
                <Text strong style={headerStyle}>
                  {t('Ubytovanie')}:
                </Text>
                <FormCustomRating name="housing_rating" disabled />
              </Col>
              <Col span={8}>
                <Text strong style={headerStyle}>
                  {t('Voľný čas')}:
                </Text>
                <FormCustomRating name="free_time_rating" disabled />
              </Col>
            </Row>
            <Row gutter={[24, 24]} style={rowStyle}>
              <Col span={8}>
                <Text strong style={headerStyle}>
                  {t('Jedlo')}:
                </Text>
                <FormCustomRating name="food_rating" disabled />
              </Col>
              <Col span={8}>
                <Text strong style={headerStyle}>
                  {t('Transport')}:
                </Text>
                <FormCustomRating name="transport_rating" disabled />
              </Col>
              <Col span={8}>
                <Text strong style={headerStyle}>
                  {t('Financie')}:
                </Text>
                <FormCustomRating name="finance_rating" disabled />
              </Col>
            </Row>
          </Form>
        </CollapseBlock>

        {variant === 'drawer' && (
          <CollapseBlock>
            <Text strong style={headerStyle}>
              {t('Ponúkané predmety')}:
            </Text>
            <ErasmusSubjectsTable
              showMapsColumn
              erasmusSubjects={university.subjects}
              pivot={university.erasmus_subjects_pivot}
              showUniversityColumn={false}
              onPreviewClick={handlePreviewSubject}
            />
          </CollapseBlock>
        )}
      </div>
    );
  };

  // ===== samotné renderovanie podľa variantu =====
  if (variant === 'drawer') {
    return (
      <Drawer
        open={!!visible}
        onClose={toggleModal}
        size={width ?? 900}
        title={title || t('Detail univerzity')}
        destroyOnHidden
        placement="right"
        push={false}
      >
        <Body />

        <Drawer
          title={
            <Space>
              <span>{previewData?.name || t('Detail predmetu')}</span>
            </Space>
          }
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          size={500}
          placement="right"
          destroyOnHidden
          getContainer={false}
          style={{ position: 'absolute' }}
        >
          {previewData ? (
            <Space orientation="vertical" size={24} style={{ width: '100%' }}>
              <Descriptions
                bordered
                column={1}
                size="middle"
                styles={{ label: { width: 220 } }}
              >
                <Descriptions.Item label={t('Fakulta')}>
                  {previewData.faculty === 'FRI' ? (
                    <Tag color="gold">FRI</Tag>
                  ) : (
                    <Tag>UNIZA</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t('Akademický rok')}>
                  {previewData.academic_year || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('Kredity')}>
                  {previewData.credits || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('Uznané kredity')}>
                  {previewData.recognized_credits || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('Priemer známok')}>
                  {previewData.marks_average || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('Rozsah')}>
                  {previewData.range || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('Univerzita')}>
                  {previewData.university_name || '-'}
                </Descriptions.Item>
              </Descriptions>

              <div>
                <Title level={5} style={{ marginBottom: 12 }}>
                  {t('Mapovanie na FRI')}
                </Title>
                {previewData.mappings?.length ? (
                  <Table
                    size="small"
                    rowKey={(r) => r.id}
                    columns={mappingColumns}
                    dataSource={previewData.mappings}
                    pagination={false}
                    bordered
                  />
                ) : (
                  <Text type="secondary">
                    {t('Žiadne mapovanie na FRI zatiaľ neexistuje.')}
                  </Text>
                )}
              </div>

              {(!previewData.mappings || previewData.mappings.length === 0) &&
                (previewData.local_subject_code ||
                  previewData.local_subject_name) && (
                  <Descriptions
                    bordered
                    column={1}
                    size="middle"
                    styles={{ label: { width: 220 } }}
                  >
                    {previewData.local_subject_code && (
                      <Descriptions.Item label={t('Kód predmetu FRI')}>
                        {previewData.local_subject_code}
                      </Descriptions.Item>
                    )}
                    {previewData.local_subject_name && (
                      <Descriptions.Item label={t('Názov predmetu FRI')}>
                        {previewData.local_subject_name}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                )}
            </Space>
          ) : null}
        </Drawer>
      </Drawer>
    );
  }

  return (
    <Modal
      open={!!visible}
      onCancel={toggleModal}
      footer={null}
      width={width ?? 1000}
      title={title || t('Detail univerzity')}
      destroyOnHidden
    >
      <Body />
    </Modal>
  );
};

UniversityModal.propTypes = {
  visible: PropTypes.bool,
  toggleModal: PropTypes.func,
  title: PropTypes.string,
  id: PropTypes.any,
  university: PropTypes.object,
  isLoading: PropTypes.bool,
  variant: PropTypes.oneOf(['modal', 'drawer']),
  width: PropTypes.number,
};

export default UniversityModal;
